// =============================================================================
// GAME SCREEN - Головний екран гри з логікою
// =============================================================================

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type {
  GameState,
  Option,
  Message,
  DecisionRecord,
  DonationCampaign,
} from '../types';
import { INITIAL_PARAMETERS, MAX_DECISIONS } from '../types';
import {
  initGame,
  getNextScenarioStream,
  generateEndingStream,
  FALLBACK_SCENARIO,
  trackDecision,
  trackPageExit,
  trackRetry,
  trackActionStripView,
} from '../services';
import { ParametersBar } from './ParametersBar';
import { SituationBriefing } from './SituationBriefing';
import { DecisionList } from './DecisionCard';
import { LoadingAnimation } from './LoadingAnimation';
import { EndingScreen } from './EndingScreen';
import { GameOverScreen } from './GameOverScreen';
import { ActionStrip } from './ActionStrip';
import { FeedbackModal } from './FeedbackModal';
import { pageTransition } from '../utils/animations';
import {
  trackFeedbackModalOpen,
  trackFeedbackModalDismiss,
} from '../services/analytics';

interface GameScreenProps {
  onOpenMenu: () => void;
  onNewGame: () => void;
  onViewRules?: () => void;
  onCreateGame?: () => void;
}

/**
 * Застосувати вплив рішення на параметри
 * Includes NaN protection to handle corrupted state or undefined impact values
 */
function applyImpact(
  state: GameState,
  impact: Option['impact'],
): Pick<
  GameState,
  'militaryStrength' | 'economy' | 'resilience' | 'westernSupport' | 'trust'
> {
  // Safe addition that treats NaN/undefined as 0
  const safeAdd = (current: number, delta: number | undefined): number => {
    const safeCurrent = Number.isNaN(current) ? 50 : current; // Default to 50 if corrupted
    const safeDelta = Number.isNaN(delta) || delta === undefined ? 0 : delta;
    return safeCurrent + safeDelta;
  };

  const clamp = (value: number): number => {
    // If result is somehow NaN, return 50 as safe default
    if (Number.isNaN(value)) {
      console.error(
        '[GameScreen] NaN detected in stats calculation, using default value',
      );
      return 50;
    }
    return Math.max(0, Math.min(100, value));
  };

  return {
    militaryStrength: clamp(
      safeAdd(state.militaryStrength, impact.militaryStrength),
    ),
    economy: clamp(safeAdd(state.economy, impact.economy)),
    resilience: clamp(safeAdd(state.resilience, impact.resilience)),
    westernSupport: clamp(safeAdd(state.westernSupport, impact.westernSupport)),
    trust: clamp(safeAdd(state.trust, impact.trust)),
  };
}

export function GameScreen({
  onOpenMenu,
  onNewGame,
  onViewRules,
  onCreateGame,
}: Readonly<GameScreenProps>) {
  const ACTION_STRIP_SESSION_KEY = 'war-game-action-strip-shown';
  const PROGRESS_KEY = 'war-game-progress';

  // Головний стан гри
  const [gameState, setGameState] = useState<GameState>(() => {
    // Try to load from local storage
    const saved = localStorage.getItem(PROGRESS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed.gameState,
          isLoading: false,
          error: null,
        };
      } catch (e) {
        console.error('Failed to parse saved progress', e);
      }
    }

    return {
      phase: 'playing',
      decisionNumber: 1,
      currentScenario: null, // Буде завантажено з API
      ...INITIAL_PARAMETERS,
      conversationHistory: [],
      decisionHistory: [],
      isLoading: true, // Починаємо з завантаження
      error: null,
    };
  });

  // Стан для фінального підсумку
  const [endingSummary, setEndingSummary] = useState<string>('');
  const [consequences, setConsequences] = useState<string | undefined>(() => {
    const saved = localStorage.getItem(PROGRESS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved).consequences;
      } catch {
        return undefined;
      }
    }
    return undefined;
  });
  const [maxDecisions, setMaxDecisions] = useState<number>(() => {
    const saved = localStorage.getItem(PROGRESS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved).maxDecisions || MAX_DECISIONS;
      } catch {
        return MAX_DECISIONS;
      }
    }
    return MAX_DECISIONS;
  });

  const [streamText, setStreamText] = useState<string>('');
  const streamAbortRef = useRef<AbortController | null>(null);
  const lastDecisionTimeRef = useRef<number>(Date.now());
  const gameStartTimeRef = useRef<number>(Date.now());

  // Action Strip state (one-time per session)
  const [showActionStrip, setShowActionStrip] = useState(false);
  const actionStripShownRef = useRef(false);

  // Feedback modal state
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const handleFeedbackOpen = useCallback(() => {
    trackFeedbackModalOpen('stalemate');
    setIsFeedbackOpen(true);
  }, []);

  const handleFeedbackClose = useCallback(() => {
    if (isFeedbackOpen) {
      trackFeedbackModalDismiss('stalemate');
    }
    setIsFeedbackOpen(false);
  }, [isFeedbackOpen]);

  // Campaign state
  const [donationCampaign] = useState<DonationCampaign | null>(() => {
    // Load from local storage (populated by App.tsx from short link or previous session)
    const stored = localStorage.getItem('war-game-donation-campaign');
    if (stored) {
      try {
        return JSON.parse(stored) as DonationCampaign;
      } catch {
        return null;
      }
    }

    return null;
  });

  /**
   * Завантаження початкового сценарію з API (тільки якщо немає збереженого)
   */
  useEffect(() => {
    if (gameState.currentScenario) return;

    async function loadInitialScenario() {
      try {
        const { scenario, config } = await initGame();
        setMaxDecisions(config.maxDecisions);
        setGameState((prev) => ({
          ...prev,
          currentScenario: scenario,
          ...config.initialParameters,
          isLoading: false,
        }));
      } catch (error) {
        console.error('Error loading initial scenario:', error);
        setGameState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Не вдалося завантажити гру',
          currentScenario: FALLBACK_SCENARIO,
        }));
      }
    }

    loadInitialScenario();
  }, [gameState.currentScenario]);

  /**
   * Збереження прогресу при зміні стану
   */
  useEffect(() => {
    if (gameState.phase !== 'playing') {
      localStorage.removeItem(PROGRESS_KEY);
      return;
    }

    // Тільки зберігаємо коли не завантажуємось, щоб уникнути дублювання ефектів при рефреші
    if (gameState.isLoading || !gameState.currentScenario) return;

    const timer = setTimeout(() => {
      localStorage.setItem(
        PROGRESS_KEY,
        JSON.stringify({
          gameState,
          consequences,
          maxDecisions,
        }),
      );
    }, 500); // Debounce saves

    return () => clearTimeout(timer);
  }, [gameState, consequences, maxDecisions]);

  useEffect(() => {
    // Sync in-memory guard from session storage (so refresh won't re-show)
    actionStripShownRef.current =
      sessionStorage.getItem(ACTION_STRIP_SESSION_KEY) === 'true';
  }, []);

  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  // Track page exit/abandon
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (gameState.phase === 'playing' && gameState.decisionNumber > 1) {
        trackPageExit({
          decisionNumber: gameState.decisionNumber,
          phase: 'game',
          resourceLevels: {
            militaryStrength: gameState.militaryStrength,
            economy: gameState.economy,
            resilience: gameState.resilience,
            westernSupport: gameState.westernSupport,
            trust: gameState.trust,
          },
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gameState]);
  /**
   * Обробка вибору гравця
   */
  const handleDecision = useCallback(
    async (option: Option) => {
      if (gameState.isLoading || !gameState.currentScenario) return;

      streamAbortRef.current?.abort();
      streamAbortRef.current = new AbortController();
      setStreamText('');

      // Track this decision with timing
      const timeSpentSeconds = Math.round(
        (Date.now() - lastDecisionTimeRef.current) / 1000,
      );
      const optionIndex = gameState.currentScenario.options.findIndex(
        (o) => o.text === option.text,
      );
      trackDecision({
        decisionNumber: gameState.decisionNumber,
        maxDecisions,
        optionIndex,
        timeSpentSeconds,
        resourceLevels: {
          militaryStrength: gameState.militaryStrength,
          economy: gameState.economy,
          resilience: gameState.resilience,
          westernSupport: gameState.westernSupport,
          trust: gameState.trust,
        },
      });
      lastDecisionTimeRef.current = Date.now();

      // Записуємо рішення в історію
      const newDecisionRecord: DecisionRecord = {
        decisionNumber: gameState.decisionNumber,
        date: gameState.currentScenario.date,
        chosenOption: option,
        consequences: '', // буде заповнено після відповіді Claude
      };

      // Оновлюємо параметри
      const newParameters = applyImpact(gameState, option.impact);

      // Створюємо повідомлення для Claude
      const userMessage: Message = {
        role: 'user',
        content: `Рішення #${gameState.decisionNumber}: ${option.text}`,
      };

      // Перевіряємо чи це останнє рішення
      if (gameState.decisionNumber >= maxDecisions) {
        // Гра завершена - генеруємо фінал
        setGameState((prev) => ({
          ...prev,
          ...newParameters,
          isLoading: true,
          decisionHistory: [...prev.decisionHistory, newDecisionRecord],
        }));

        try {
          setEndingSummary('');
          const summary = await generateEndingStream(
            [...gameState.conversationHistory, userMessage],
            newParameters,
            undefined,
            {
              signal: streamAbortRef.current.signal,
              onDelta: (delta) => {
                setStreamText((prev) => prev + delta);
                setEndingSummary((prev) => prev + delta);
              },
            },
          );
          setEndingSummary(summary);
          setGameState((prev) => ({
            ...prev,
            phase: 'ending',
            isLoading: false,
          }));
        } catch {
          setEndingSummary(
            'Ваша гра завершена. Ви прийняли 20 критичних рішень, що визначили долю України.',
          );
          setGameState((prev) => ({
            ...prev,
            phase: 'ending',
            isLoading: false,
          }));
        }
        return;
      }

      // Продовжуємо гру - отримуємо новий сценарій
      setGameState((prev) => ({
        ...prev,
        ...newParameters,
        isLoading: true,
        error: null,
      }));

      try {
        const result = await getNextScenarioStream(
          [...gameState.conversationHistory, userMessage],
          option.text,
          gameState.decisionNumber,
          undefined,
          {
            militaryStrength: newParameters.militaryStrength,
            economy: newParameters.economy,
            resilience: newParameters.resilience,
            westernSupport: newParameters.westernSupport,
            trust: newParameters.trust,
          },
          {
            signal: streamAbortRef.current.signal,
            onDelta: (delta: string) => {
              setStreamText((prev) => prev + delta);
            },
          },
        );

        // Handle game over
        if (result.type === 'gameOver') {
          setGameState((prev) => ({
            ...prev,
            phase: 'gameOver',
            gameOverType: result.response.type,
            isLoading: false,
          }));
          return;
        }

        // Normal scenario response
        const { scenario, consequences: newConsequences } = result;

        // Trigger Action Strip based on resource thresholds or turn count (once per session)
        const hasLowResource = Object.values(newParameters).some(
          (val) => val < 35,
        );
        const isTargetDecision = gameState.decisionNumber >= 11;

        if (
          donationCampaign &&
          !actionStripShownRef.current &&
          (hasLowResource || isTargetDecision)
        ) {
          actionStripShownRef.current = true;
          sessionStorage.setItem(ACTION_STRIP_SESSION_KEY, 'true');
          setShowActionStrip(true);
          // Track action strip visibility
          trackActionStripView(
            gameState.decisionNumber,
            donationCampaign.type === 'petition' ? 'petition' : 'donation',
          );
        }

        // Оновлюємо стан з новим сценарієм
        setConsequences(newConsequences);
        setGameState((prev) => ({
          ...prev,
          decisionNumber: prev.decisionNumber + 1,
          currentScenario: scenario,
          conversationHistory: [
            ...prev.conversationHistory,
            userMessage,
            {
              role: 'assistant',
              content: JSON.stringify({
                consequences: newConsequences,
                newSituation: scenario,
              }),
            },
          ],
          decisionHistory: [
            ...prev.decisionHistory,
            { ...newDecisionRecord, consequences: newConsequences },
          ],
          isLoading: false,
        }));
      } catch (error) {
        console.error('Error getting next scenario:', error);

        // Fallback - показуємо помилку і пропонуємо retry
        setGameState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Невідома помилка',
          currentScenario: {
            ...FALLBACK_SCENARIO,
            decisionNumber: prev.decisionNumber + 1,
          },
        }));
      }
	    },
	    [gameState, maxDecisions, donationCampaign],
	  );

  /**
   * Обробка retry після помилки
   */
  const handleRetry = useCallback(async () => {
    // Track retry attempt
    trackRetry('game', gameState.decisionNumber);

    setGameState((prev) => ({
      ...prev,
      error: null,
      isLoading: true,
    }));

	    try {
	      const { scenario, config } = await initGame();
	      setMaxDecisions(config.maxDecisions);
	      setGameState((prev) => ({
	        ...prev,
        currentScenario: scenario,
        decisionNumber: 1,
        ...config.initialParameters,
        conversationHistory: [],
        decisionHistory: [],
        isLoading: false,
      }));
    } catch (error) {
      setGameState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'Не вдалося завантажити гру',
        currentScenario: FALLBACK_SCENARIO,
      }));
	    }
	  }, [gameState.decisionNumber]);

  // Dismiss action strip (called on any user action)
  const handleActionStripDismiss = useCallback(() => {
    actionStripShownRef.current = true;
    sessionStorage.setItem(ACTION_STRIP_SESSION_KEY, 'true');
    setShowActionStrip(false);
  }, []);

  // Якщо гра програна - показуємо GameOverScreen
  if (gameState.phase === 'gameOver' && gameState.gameOverType) {
    return (
      <GameOverScreen
        type={gameState.gameOverType}
        decisionNumber={gameState.decisionNumber}
        finalStats={{
          militaryStrength: gameState.militaryStrength,
          economy: gameState.economy,
          resilience: gameState.resilience,
          westernSupport: gameState.westernSupport,
          trust: gameState.trust,
        }}
        onRestart={onNewGame}
      />
    );
  }

  // Якщо гра завершена - показуємо EndingScreen
  if (gameState.phase === 'ending') {
    const totalTimeSeconds = Math.round(
      (Date.now() - gameStartTimeRef.current) / 1000,
    );
    return (
      <EndingScreen
        militaryStrength={gameState.militaryStrength}
        economy={gameState.economy}
        resilience={gameState.resilience}
        westernSupport={gameState.westernSupport}
        trust={gameState.trust}
        endingSummary={endingSummary}
        totalTimeSeconds={totalTimeSeconds}
        onRestart={onNewGame}
      />
    );
  }

  return (
    <div className="min-h-screen px-4 py-6 md:py-8">
      <div className="max-w-3xl mx-auto relative z-10">
        {/* Parameters bar - завжди видимий */}
        <div className="mb-6 sticky top-4 z-20">
	          <ParametersBar
	            militaryStrength={gameState.militaryStrength}
	            economy={gameState.economy}
	            resilience={gameState.resilience}
	            westernSupport={gameState.westernSupport}
	            trust={gameState.trust}
	            decisionNumber={gameState.decisionNumber}
	            maxDecisions={maxDecisions}
	            onLogoClick={onOpenMenu}
	            onViewRules={onViewRules}
	            onCreateGame={onCreateGame}
	            onShare={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'Головнокомандувач',
                  text: 'Спробуй себе в ролі Головнокомандувача!',
                  url: window.location.origin,
                });
              } else {
                navigator.clipboard.writeText(window.location.origin);
              }
            }}
            onFeedback={handleFeedbackOpen}
            isLoading={gameState.isLoading}
          />
        </div>

        {/* Error message */}
        {gameState.error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-5 border border-war-red/30 bg-war-red/5"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-war-red font-bold">! ERR:</span>
              <p className="text-war-red text-xs font-mono uppercase">
                {gameState.error}
              </p>
            </div>
            <button
              onClick={handleRetry}
              className="text-[10px] text-war-bright font-mono border border-war-bright/20 px-3 py-1 hover:bg-war-bright/10 transition-colors uppercase tracking-widest"
            >
              ПЕРЕЗАПУСТИТИ_ЧЕРГУ [RETRY]
            </button>
          </motion.div>
        )}

        {/* Main content area */}
        <AnimatePresence mode="wait">
          {gameState.isLoading ? (
            <motion.div
              key="loading"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <LoadingAnimation
                message={
                  gameState.decisionNumber >= maxDecisions
                    ? 'Підводимо підсумки...'
                    : 'Аналізуємо наслідки вашого рішення...'
                }
                streamText={streamText}
              />
            </motion.div>
          ) : (
            gameState.currentScenario && (
              <motion.div
                key={`scenario-${gameState.decisionNumber}`}
                variants={pageTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-6"
              >
                {/* Situation briefing */}
                <SituationBriefing
                  date={gameState.currentScenario.date}
                  location={gameState.currentScenario.location}
                  briefing={gameState.currentScenario.briefing}
                  urgency={gameState.currentScenario.urgency}
                  consequences={consequences}
                  decisionNumber={gameState.decisionNumber}
                />

                {/* Decision options */}
                <div className={showActionStrip ? 'pb-32' : ''}>
                  <DecisionList
                    options={gameState.currentScenario.options}
                    onSelect={handleDecision}
                    disabled={gameState.isLoading}
                  />
                </div>

                {/* Inline feedback link at the bottom */}
                <div className="mt-8 pt-4 border-t border-war-dim/10 flex justify-center">
                  <button
                    onClick={handleFeedbackOpen}
                    className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-war-dim/50 hover:text-war-dim transition-colors opacity-50 hover:opacity-75"
                    aria-label="Відгук та повідомлення про помилки"
                  >
                    {/* Communication Icon */}
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                      />
                    </svg>
                    <span>Зв'язок зі штабом</span>
                  </button>
                </div>
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>

      {/* Action Strip - appears once per session after emotional peak */}
      {donationCampaign && (
        <ActionStrip
          isVisible={showActionStrip}
          campaign={donationCampaign}
          decisionNumber={gameState.decisionNumber}
          onDismiss={handleActionStripDismiss}
        />
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={handleFeedbackClose}
        gameResult="stalemate"
        averageScore={Math.round(
          (gameState.militaryStrength +
            gameState.economy +
            gameState.resilience +
            gameState.westernSupport +
            gameState.trust) /
            5,
        )}
        decisionNumber={gameState.decisionNumber}
      />
    </div>
  );
}
